import { useEffect, useState } from 'react';
import { api } from './api';
import { PLANS as STATIC_PLANS, BILLING_CYCLES as STATIC_CYCLES } from './plans';

// In-memory cache so we don't hit /public/plans on every page render.
// First call wins; refetched on full page reload (which is when prices effectively change
// for end users — they refresh the landing page).
let _cache = null;
let _inflight = null;

function mergePlans(serverItems) {
    // Merge: keep the static perks/featured flags, override prices from server.
    return STATIC_PLANS.map((p) => {
        const server = (serverItems || []).find((s) => s.id === p.id);
        if (!server) return p;
        return {
            ...p,
            priceRon: server.price_ron != null ? server.price_ron : p.priceRon,
            originalPriceRon: server.original_price_ron != null ? server.original_price_ron : p.originalPriceRon,
            vatIncluded: server.vat_included != null ? server.vat_included : p.vatIncluded,
        };
    });
}

function mergeCycles(serverCycles) {
    return STATIC_CYCLES.map((c) => {
        const server = (serverCycles || []).find((s) => s.id === c.id);
        if (!server) return c;
        return {
            ...c,
            discountPercent: server.discount_percent != null ? server.discount_percent : c.discountPercent,
        };
    });
}

async function fetchOnce() {
    if (_cache) return _cache;
    if (_inflight) return _inflight;
    _inflight = api.get('/public/plans').then((r) => {
        _cache = {
            plans: mergePlans(r.data.items),
            cycles: mergeCycles(r.data.billing_cycles),
        };
        _inflight = null;
        return _cache;
    }).catch(() => {
        _inflight = null;
        // Fallback to static if API fails (offline, error)
        return { plans: STATIC_PLANS, cycles: STATIC_CYCLES };
    });
    return _inflight;
}

// Manual invalidation (for super-admin after they save a price change)
export function invalidateLivePlansCache() { _cache = null; }

// React hook — returns live plans + cycles (falls back to static while loading)
export function useLivePlans() {
    const [data, setData] = useState(_cache || { plans: STATIC_PLANS, cycles: STATIC_CYCLES });
    useEffect(() => {
        let mounted = true;
        fetchOnce().then((d) => { if (mounted) setData(d); });
        return () => { mounted = false; };
    }, []);
    return data;
}
